import {setPmwLangs} from './language-selector-slice';
import type {LanguageData, StateProps} from './language-selector.types';
import {getPMWLangsVersion, getSupportedLanguages} from './language-selector-thunk';

const LANG_CACHE_KEYS = {
  KEY_PMW_LANGS: 'pmw_langs',
  KEY_PMW_LANGS_VERSION: 'pmw_langs_version',
};

/**
 * saves the fetched langs and langs version into local storage cache
 * @param {Array} pmwLangs
 * @param {number} pmwLangsVersion
 */
const cachePMWLangs = (pmwLangs: Array<LanguageData>, pmwLangsVersion: string) => {
  window.PMW.setCacheItem(LANG_CACHE_KEYS.KEY_PMW_LANGS, pmwLangs);
  window.PMW.setCacheItem(LANG_CACHE_KEYS.KEY_PMW_LANGS_VERSION, pmwLangsVersion);
};

export const getPmwLangsVersionFromStore = () => {
  return getLangSelectorStore().pmwLangsVersion;
};

/**
 * returns the current PMW langs version from redux store
 * if the version is not available, it is fetched via AJAX and returned. The version is also stored into redux for further use.
 * @return {Promise<null|*>}
 */
const getPmwLangsVersion = async (): Promise<any> => {
  const pmwLangsVersion = getPmwLangsVersionFromStore();
  if (pmwLangsVersion) {
    return pmwLangsVersion;
  }

  const {version} = await window.PMW.redux.store.dispatch(getPMWLangsVersion()).unwrap();
  return version;
};

/**
 * gets pmw langs and stores them in Redux if not already loaded
 * If the langs are available within the local storage cache, then they are retrieved via cache else through AJAX
 * @param {number|null} pmwLangsVersion
 */
export const loadLanguages = async (pmwLangsVersion = null) => {
  if (areLangsAlreadyInStore()) {
    return;
  }

  if (pmwLangsVersion === null) {
    pmwLangsVersion = await getPmwLangsVersion();
  }

  const pmwLangs = window.PMW.getVersionedCacheItem(LANG_CACHE_KEYS.KEY_PMW_LANGS, LANG_CACHE_KEYS.KEY_PMW_LANGS_VERSION, pmwLangsVersion);

  if (pmwLangs) {
    window.PMW.redux.store.dispatch(setPmwLangs(pmwLangs));
  } else {
    const response = await window.PMW.redux.store.dispatch(getSupportedLanguages()).unwrap();
    cachePMWLangs(response.pmwLangs, response.version.toString());
  }
};

const areLangsAlreadyInStore = (): number => {
  return getLanguagesFromStore().length;
};

export const getLanguagesFromStore = (): LanguageData[] => {
  return getLangSelectorStore().pmwLangs;
};

export const getLangSelectorStore = (): StateProps => {
  return window.PMW.redux.store.getState().langSelector;
};
